import phoneParser, { AsYouType, CountryCode } from 'libphonenumber-js';

import { Country } from '@xemplo/country-helper';
import { safeCall } from '@xemplo/safe-call';
import { XemploInputProps } from '@xemplo/xemplo-input';

import { PhoneFieldProps } from '../phone-field.types';

/**
 * Formats the given phone number input for display based on the specified country code.
 *
 * If the input is empty or undefined, an empty string is returned.
 * If the input includes the country calling code, it is removed before formatting.
 * The formatting is done using the `AsYouType` formatter from `libphonenumber-js`.
 *
 * When the `isDeleting` flag is true, the function checks if the resulting formatted number
 * is still valid or possible. If not, it returns the unformatted trimmed input instead.
 *
 * @param input - The raw phone number input to format.
 * @param countryCode - The country code to use for formatting.
 * @param isDeleting - A flag indicating whether the user is deleting characters.
 * @returns The formatted phone number string for display.
 * @remarks This function was designed to be used on the phone-field input component.
 * As such, it has the assumption this will be called on the onChange event of an input,
 * where the user might be adding or deleting characters. Hence, this is NOT the full
 * phone number that would contain the country calling code, but rather the local part
 * of the number as the user types it.
 */
export function formatDisplayValue(
  input: XemploInputProps['value'],
  countryCode: Country.Code,
  isDeleting: boolean,
): string {
  if (!input) return '';

  // When initial input includes country calling code, remove it for formatting
  const receivedCountryCallingCode = Country.getPhoneCodeByCountryCode(countryCode);
  const trimmedInput = input
    .toString()
    .replace(receivedCountryCallingCode ?? '', '')
    .trim();

  const asYouType = new AsYouType(countryCode as CountryCode);
  const formatted = asYouType.input(trimmedInput);

  if (!isDeleting) return formatted;

  // User is deleting: check if the number is still valid or possible
  const parsed = asYouType.getNumber();
  if (parsed?.isValid() || parsed?.isPossible()) return formatted;

  // Not a possible number anymore, return the input as-is
  return trimmedInput;
}

/**
 * Attempts to parse a phone number string into a structured phone number object.
 *
 * This function safely wraps the underlying `phoneParser`, catching any errors that might occur
 * during parsing (e.g., invalid formats) and returning `undefined` instead of throwing.
 *
 * @see https://gitlab.com/catamphetamine/libphonenumber-js
 *
 * @param text - The raw phone number string to parse.
 * @param defaultCountry - An optional country code to use as a default if the phone number
 *                         does not contain an international dialing prefix.
 * @returns The parsed phone number object if successful, or `undefined` if parsing fails
 *          or an error occurs.
 */
export function tryParsePhoneNumber(text?: string, defaultCountry?: Country.Code) {
  if (!text) return undefined;

  const countryCode = defaultCountry as CountryCode | undefined;
  const [result, error] = safeCall(() => phoneParser(text, countryCode));
  if (error) return undefined;
  return result;
}

/**
 * Determines the initial country code for a phone number input.
 *
 * This function parses the input phone number and attempts to extract its country code.
 * If no input is provided or the country code cannot be determined, it falls back to
 * the default country code.
 *
 * @param input - The phone number value to parse. Can be a string, number, or undefined.
 * @param defaultCountryCode - The fallback country code to use if parsing fails or no input is provided.
 *                             Defaults to `Country.Code.Australia`.
 *
 * @returns The detected country code from the input, or the default country code if detection fails.
 *
 * @remarks
 * Uses the `AsYouType` parser which handles partial and invalid phone numbers gracefully.
 *
 * @example
 * ```typescript
 * // Returns Country.Code.United_States
 * handleInitialCountryCode('+1234567890', Country.Code.Australia);
 *
 * // Returns Country.Code.Australia (default)
 * handleInitialCountryCode('', Country.Code.Australia);
 * ```
 */
export function handleInitialCountryCode(
  input: PhoneFieldProps['value'],
  defaultCountryCode: Country.Code = Country.Code.Australia,
) {
  if (!input) return defaultCountryCode;

  // Using the AsYouType because it can deal with partial/invalid numbers better
  const asYouType = new AsYouType(defaultCountryCode as CountryCode);
  asYouType.input(input.toString());
  const phoneNumber = asYouType.getNumber();

  let initialCountryCode = defaultCountryCode;

  if (phoneNumber?.country != null) {
    initialCountryCode = phoneNumber.country as Country.Code;
  } else if (phoneNumber?.country == null && phoneNumber?.countryCallingCode != null) {
    // If country is not detected, but we have a country calling code,
    // try finding the country from the calling code
    const detectedCountryCode = Country.getCountryCodeByPhoneCode(
      phoneNumber.countryCallingCode,
    );
    if (detectedCountryCode) {
      initialCountryCode = detectedCountryCode;
    }
  }

  // If the parsed number has a country, use it
  return initialCountryCode;
}
